import React, { useCallback, useEffect, useRef, useState } from "react";
import PerfectScrollbar from "react-perfect-scrollbar";
import { makeStyles } from "@material-ui/styles";
import {
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  CircularProgress,
  Grid,
  Link,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Typography,
} from "@material-ui/core";
import { axiosInstance } from "common/network";
import { StatusBullet } from "components";
import { formatDate, formatDateTime } from "helpers/formaters";
import { downloadDidimoFormat } from "services/didimoDownload";
import DidimoDetails from "views/UserList/components/DidimoDetails";

const PAGE_SIZE = 10;

// Maps a didimo status to a StatusBullet colour. Unknown statuses fall back to neutral.
const statusColors = {
  done: "success",
  completed: "success",
  processing: "info",
  pending: "info",
  in_progress: "info",
  error: "danger",
  deleted: "danger",
};

const STATUS_OPTIONS = [
  { value: "", label: "Any status" },
  { value: "done", label: "Done" },
  { value: "processing", label: "Processing" },
  { value: "pending", label: "Pending" },
  { value: "error", label: "Error" },
  { value: "deleted", label: "Deleted" },
];

const ORDER_OPTIONS = [
  { value: "-created_at", label: "Newest first" },
  { value: "created_at", label: "Oldest first" },
];

const EMPTY_FILTERS = {
  key: "",
  accountEmail: "",
  accountUuid: "",
  status: "",
  dgp: "",
  minCreatedAt: "",
  maxCreatedAt: "",
  orderBy: "-created_at",
};

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
  },
  filters: {
    marginBottom: theme.spacing(2),
  },
  filterActions: {
    display: "flex",
    gap: theme.spacing(1),
    alignItems: "center",
    marginTop: theme.spacing(1),
  },
  content: { padding: 0 },
  inner: { minWidth: 1000 },
  actions: { justifyContent: "flex-end" },
  statusContainer: {
    display: "flex",
    alignItems: "center",
    textTransform: "capitalize",
  },
  status: { marginRight: theme.spacing(1) },
  key: { fontFamily: "monospace" },
  accountEmail: { fontSize: 13 },
  accountUuid: { fontFamily: "monospace", fontSize: 11, color: "rgba(0,0,0,0.5)" },
  notAvailable: { color: "rgba(0,0,0,0.4)", fontStyle: "italic" },
  empty: { padding: theme.spacing(3) },
  formats: { display: "flex", flexWrap: "wrap", gap: theme.spacing(1) },
  formatButton: { textTransform: "none", minWidth: 0 },
}));

const DidimosSearch = () => {
  const classes = useStyles();

  // Field values being edited (do not trigger a fetch until "Search").
  const [fields, setFields] = useState(EMPTY_FILTERS);
  // The filters actually applied to the current results.
  const [applied, setApplied] = useState(EMPTY_FILTERS);

  const [didimos, setDidimos] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0); // 0-based; API is 1-based.
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [downloading, setDownloading] = useState({});
  const [selected, setSelected] = useState(null);
  // Monotonic id of the most recent fetch. Responses from superseded fetches (e.g. two quick
  // ordering changes racing) are ignored, so only the latest request's results are ever applied.
  const requestSeq = useRef(0);

  const setField = (name) => (event) =>
    setFields((f) => ({ ...f, [name]: event.target.value }));

  const fetchDidimos = useCallback(async () => {
    const seq = ++requestSeq.current;
    setLoading(true);
    setError(false);
    try {
      const params = {
        all_accounts: true,
        page: page + 1,
        page_size: PAGE_SIZE,
        order_by: applied.orderBy,
      };
      if (applied.key) params.key = applied.key;
      if (applied.accountEmail) params.account_email = applied.accountEmail;
      if (applied.accountUuid) params.account_uuid = applied.accountUuid;
      if (applied.status) params.status = applied.status;
      if (applied.dgp) params.dgp = applied.dgp;
      if (applied.minCreatedAt) params.min_created_at = applied.minCreatedAt;
      if (applied.maxCreatedAt) params.max_created_at = applied.maxCreatedAt;

      const response = await axiosInstance.get(
        process.env.REACT_APP_API_ROOT + "v3/didimos",
        { params }
      );
      // Drop the result if a newer fetch has since started (avoids out-of-order overwrites).
      if (seq !== requestSeq.current) return;

      // De-duplicate by key: rendering the same didimo twice (duplicate React keys) breaks list
      // reconciliation and leaves ghost rows that pile up until a refresh. Guard against it.
      const list = response?.data?.didimos || [];
      const seen = new Set();
      const deduped = list.filter((d) => {
        if (!d?.key) return true;
        if (seen.has(d.key)) return false;
        seen.add(d.key);
        return true;
      });
      setDidimos(deduped);
      setTotal(response?.data?.total_size || 0);
    } catch (err) {
      if (seq !== requestSeq.current) return;
      console.log("fetchDidimos:", err);
      setError(true);
      setDidimos([]);
      setTotal(0);
    } finally {
      if (seq === requestSeq.current) setLoading(false);
    }
  }, [applied, page]);

  useEffect(() => {
    fetchDidimos();
  }, [fetchDidimos]);

  const handleSearch = () => {
    setPage(0);
    setApplied(fields);
  };

  const handleClear = () => {
    setFields(EMPTY_FILTERS);
    setPage(0);
    setApplied(EMPTY_FILTERS);
  };

  const onFilterKeyDown = (event) => {
    if (event.key === "Enter") handleSearch();
  };

  const downloadFormat = async (didimoKey, format) => {
    if (!format?.__links?.self) return;
    const marker = `${didimoKey}:${format.name}`;
    setDownloading((d) => ({ ...d, [marker]: true }));
    try {
      await downloadDidimoFormat(format);
    } catch (err) {
      console.log("downloadFormat:", err);
      alert(`Could not download "${format.name}". Please try again.`);
    } finally {
      setDownloading((d) => {
        const next = { ...d };
        delete next[marker];
        return next;
      });
    }
  };

  return (
    <div className={classes.root}>
      <Typography variant="h4" gutterBottom>
        Didimos
      </Typography>

      <Card className={classes.filters}>
        <CardContent>
          <Grid container spacing={2} onKeyDown={onFilterKeyDown}>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="Didimo key"
                placeholder="e.g. abc* (wildcard)"
                value={fields.key}
                onChange={setField("key")}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="Account email"
                value={fields.accountEmail}
                onChange={setField("accountEmail")}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="Account UUID"
                value={fields.accountUuid}
                onChange={setField("accountUuid")}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                select
                label="Status"
                value={fields.status}
                onChange={setField("status")}
                fullWidth
                size="small"
              >
                {STATUS_OPTIONS.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="DGP"
                value={fields.dgp}
                onChange={setField("dgp")}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="Created after"
                type="date"
                value={fields.minCreatedAt}
                onChange={setField("minCreatedAt")}
                fullWidth
                size="small"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="Created before"
                type="date"
                value={fields.maxCreatedAt}
                onChange={setField("maxCreatedAt")}
                fullWidth
                size="small"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                select
                label="Order"
                value={fields.orderBy}
                onChange={setField("orderBy")}
                fullWidth
                size="small"
              >
                {ORDER_OPTIONS.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>
          <div className={classes.filterActions}>
            <Button variant="contained" color="primary" onClick={handleSearch}>
              Search
            </Button>
            <Button color="primary" onClick={handleClear}>
              Clear
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader
          title="Results"
          subheader={total > 0 ? `${total} total` : undefined}
        />
        <CardContent className={classes.content}>
          <PerfectScrollbar>
            <div className={classes.inner}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Didimo</TableCell>
                    <TableCell>Account</TableCell>
                    <TableCell>Input type</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell>Expires</TableCell>
                    <TableCell>Downloads</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody className={loading ? "loading" : ""}>
                  {didimos.map((didimo) => {
                    const formats = didimo.transfer_formats || [];
                    return (
                      <TableRow hover key={didimo.key}>
                        <TableCell className={classes.key}>
                          <Link
                            component="button"
                            variant="body2"
                            className={classes.key}
                            onClick={() => setSelected(didimo)}
                          >
                            {didimo.key}
                          </Link>
                        </TableCell>
                        <TableCell>
                          {didimo.account ? (
                            <>
                              <div className={classes.accountEmail}>
                                {didimo.account.email || "—"}
                              </div>
                              <div className={classes.accountUuid}>
                                {didimo.account.uuid}
                              </div>
                            </>
                          ) : (
                            <Typography className={classes.notAvailable}>—</Typography>
                          )}
                        </TableCell>
                        <TableCell>{didimo.input_type || "—"}</TableCell>
                        <TableCell>
                          <div className={classes.statusContainer}>
                            <StatusBullet
                              className={classes.status}
                              color={statusColors[didimo.status] || "neutral"}
                              size="sm"
                            />
                            {didimo.status}
                          </div>
                        </TableCell>
                        <TableCell>
                          {didimo.created_at ? formatDateTime(didimo.created_at) : "—"}
                        </TableCell>
                        <TableCell>
                          {didimo.expires_at ? formatDate(didimo.expires_at) : "—"}
                        </TableCell>
                        <TableCell>
                          {formats.length > 0 ? (
                            <div className={classes.formats}>
                              {formats.map((format) => {
                                const marker = `${didimo.key}:${format.name}`;
                                const busy = !!downloading[marker];
                                return (
                                  <Button
                                    key={format.name}
                                    className={classes.formatButton}
                                    variant="outlined"
                                    color="primary"
                                    size="small"
                                    disabled={busy}
                                    onClick={() => downloadFormat(didimo.key, format)}
                                    startIcon={
                                      busy ? <CircularProgress size={14} /> : undefined
                                    }
                                  >
                                    {format.name}
                                  </Button>
                                );
                              })}
                            </div>
                          ) : (
                            <Typography className={classes.notAvailable}>—</Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
              {!loading && didimos.length === 0 && (
                <Typography className={classes.empty}>
                  {error
                    ? "Could not load didimos."
                    : "No didimos match these filters."}
                </Typography>
              )}
            </div>
          </PerfectScrollbar>
        </CardContent>
        {total > 0 && (
          <CardActions className={classes.actions}>
            <TablePagination
              component="div"
              count={total}
              onChangePage={(event, newPage) => setPage(newPage)}
              onChangeRowsPerPage={() => {}}
              page={page}
              rowsPerPage={PAGE_SIZE}
              rowsPerPageOptions={[PAGE_SIZE]}
              showFirstButton={true}
              showLastButton={true}
            />
          </CardActions>
        )}
      </Card>

      <DidimoDetails
        didimo={selected}
        open={!!selected}
        onClose={() => setSelected(null)}
      />
    </div>
  );
};

export default DidimosSearch;
